// Only in Legacy View

import React, { useMemo, useState } from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useNavigation } from "@react-navigation/native";
import { useRoute } from "@react-navigation/native";
import { useAuth } from "../../../hooks/useAuth";
import { useVisitSession } from "../../../contexts/VisitSessionContext";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useDailyLimitQuery, useSchoolsQuery } from "../../../graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";
import useWebSocket from "../../../hooks/useWebSocket";
import MainMenu from "../../../components/navigation/MainMenu/MainMenu";
import { createCheckoutSession } from "../../../services";
import handleOpenLink from "../../../utils/handleOpenLink";
import {
  DashboardIcon,
  LearnIcon,
  HealthIcon,
  TodosIcon,
  EducationIcon,
  CareerIcon,
  MeetingIcon,
} from "../../../components/svgs/menuIcons";
import { ChartBar } from "@phosphor-icons/react";

import FeedbackModal from "../../../components/navigation/FeedbackModal/FeedbackModal";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { RolePermissions } from "../../../constants/rolesAndPermissions";

const ChartBarIcon = ({ fill, ...props }) => <ChartBar color={fill} size={24} {...props} weight="bold" />;

type MenuItem = {
  name: keyof RootStackParamList;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  externalURL: string | undefined;
  index: number;
};

const constantMenuItems: MenuItem[] = [
  {
    name: "Home",
    icon: DashboardIcon,
    externalURL: undefined,
    index: 0,
  },
];

function MainMenuContainer() {
  const appConfig = useAppConfig();
  const { sendMessage } = useWebSocket();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const { logout, meData, userInfo } = useAuth();
  const { endSession } = useVisitSession();
  const { state, dispatch } = useAppState();

  const { data: dailyLimitData } = useDailyLimitQuery();
  const { data: schoolsData, loading: schoolsLoading } = useSchoolsQuery();

  const [bugReportModalVisible, setBugReportModalVisible] = useState(false);

  const { additionalScope, scope, showFeedback, isLMS, isLTI, isFreemium, showMiniLogo, key, logo, miniLogo } =
    appConfig;

  const additionalScopes = additionalScope?.map((item) => JSON.parse(item));

  const additionalScopeMenuItems =
    additionalScopes?.map((item) => ({
      name: item.name as keyof RootStackParamList,
      icon: MeetingIcon,
      externalURL: item.externalURL,
      index: item.index,
    })) || [];

  const DomainConfigMenuItems: MenuItem[] = [
    { name: "Activities", icon: EducationIcon, externalURL: undefined, index: 5 },
    { name: "Coursework", icon: EducationIcon, externalURL: undefined, index: 6 },
    // { name: "Tools", icon: JournalIcon, externalURL: undefined, index: 7 },
    { name: "Learn", icon: LearnIcon, externalURL: undefined, index: 1 },
    { name: "Career", icon: CareerIcon, externalURL: undefined, index: 98 },
    ...(userInfo.userPermissions.includes(RolePermissions.analyticsViewSchool)
      ? [
          {
            name: "Session Analytics" as keyof RootStackParamList,
            icon: ChartBarIcon,
            externalURL: undefined,
            index: 100,
          },
        ]
      : []),
  ];

  const menuItems = useMemo(
    () =>
      [
        ...constantMenuItems,
        ...DomainConfigMenuItems.filter((item) => scope.includes(item.name)),
        ...additionalScopeMenuItems,
      ]
        .filter((item) => {
          if (item.name === "Coursework" || item.name === "Activities") {
            return schoolsData?.getSchools?.length > 0 && !schoolsLoading;
          }
          return true;
        })
        .sort((a, b) => a.index - b.index),
    [scope, additionalScope, schoolsData, schoolsLoading]
  );

  const handelUpgradePress = async () => {
    let newWindow;
    if (Platform.OS === "web") {
      newWindow = window.open("", "_blank");
    }

    const token = await AsyncStorage.getItem("accessToken");
    const data = await createCheckoutSession(token);

    const { url } = data;

    await handleOpenLink(url, newWindow);
  };

  const handleItemPress = (item) => {
    if (item.externalURL) {
      let newWindow;
      if (Platform.OS === "web") {
        newWindow = window.open("", "_blank");
      }
      handleOpenLink(item.externalURL, newWindow);
    } else if (item.name === "Coursework" || item.name === "Activities") {
      handleCourseworkNavigation();
    } else {
      navigation.navigate(item.name);
      handleNavigate();
    }
  };

  const handleCourseworkNavigation = () => {
    if (schoolsData?.getSchools && schoolsData.getSchools.length === 1) {
      navigation.navigate("School", {
        integration_id: schoolsData.getSchools[0].id,
      });
    } else {
      navigation.navigate("Coursework");
    }
    handleNavigate();
  };

  const handleFeedbackPress = async () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });

    setBugReportModalVisible(true);
  };

  const handleCloseBugReportModal = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setBugReportModalVisible(false);
  };

  const handleSettingsPress = () => {
    navigation.navigate("Settings");
    handleNavigate();
  };

  const handleLogoutPress = async () => {
    await endSession();
    logout();
  };

  const handleNavigate = async () => {
    const token = await AsyncStorage.getItem("accessToken");
    sendMessage({
      meta: {
        token: token,
        abort: true,
      },
    });

    dispatch({
      type: actions.SET_META,
      payload: {
        isMainMenuExpanded: false,
        isLearnMenuOpen: false,
        isCustomCourseworkMenuOpen: false,
      },
    });
  };

  const showLearnSectionOrName = (name) => {
    if (name === "Tracks" || name === "Categories" || name === "Track Overview") return "Learn";
    if (name === "School") return "Coursework";
    return name;
  };

  return (
    <>
      <MainMenu
        menuItems={menuItems}
        selectedItem={showLearnSectionOrName(route.name)}
        currentDomain={key}
        showMiniLogo={showMiniLogo}
        messageCount={state?.meta?.dailyRequestNumber || dailyLimitData?.getDailyLimit.dailyRequests}
        userInitial={userInfo.userInitial}
        isPro={meData?.me?.isPro}
        isFreemium={isFreemium}
        isLMS={isLMS}
        isLTI={isLTI}
        showFeedback={showFeedback}
        onUpgradePress={handelUpgradePress}
        onItemPress={handleItemPress}
        onFeedbackPress={handleFeedbackPress}
        onSettingsPress={handleSettingsPress}
        onLogoutPress={handleLogoutPress}
        logo={logo}
        miniLogo={miniLogo}
      />
      <div className="js-rapidr-board-btn" style={{ display: "none" }}></div>
      <FeedbackModal visible={bugReportModalVisible} onClose={handleCloseBugReportModal} />
    </>
  );
}

export default MainMenuContainer;
